import { type ReactNode, useMemo } from 'react';
import path from 'ramda/es/path';
import fromPairs from 'ramda/es/fromPairs';
import isEmpty from 'ramda/es/isEmpty';
import isNil from 'ramda/es/isNil';

import { useFilterApplied, type StateByFilterId } from 'app/common/ui-components/filter';
import type { Params } from 'app/dataTypes/OrgSettingsChanges.types';

import type { FilterParams } from './FIlterParams.type';
import periodFilter from './period';
import sourceFilter from './source';
import actorFilter from './actor';


const convert = {
  from: path([periodFilter, 0]),
  to: path([periodFilter, 1]),
  sourceId: path([sourceFilter, 0]),
  actorId: path([actorFilter, 0]),
};

const isPresent = (value: unknown): value is string => {
  return !isNil(value) && !isEmpty(value as Array<unknown> | Record<string, unknown> | string);
};

type AppliedPairs = Array<[string, Array<string>]>;
type AppliedState = StateByFilterId | AppliedPairs;

type ConvertedParams = Partial<Pick<Params, 'from' | 'to' | 'sourceId' | 'actorId'>>;

const createConverter = (
  source: StateByFilterId,
  converters: typeof convert,
): ConvertedParams => {
  return (Object.keys(converters) as Array<keyof typeof convert>).reduce(
    (acc, key) => {
      const value = converters[key](source);
      if (!isPresent(value)) {
        return acc;
      }
      acc[key] = value as ConvertedParams[typeof key];
      return acc;
    },
    {} as ConvertedParams,
  );
};


type Props = {
  children: (params: Params) => ReactNode,
} & FilterParams

const HistoryFilterParams = ({ children, ...others }: Props) => {
  const applied = useFilterApplied() as AppliedState | null;

  const appliedState: StateByFilterId | null = useMemo(() => {
    if (!applied) {
      return null;
    }
    if (Array.isArray(applied)) {
      return fromPairs(applied) as StateByFilterId;
    }
    return applied;
  }, [applied]);

  const params: Params = useMemo(() => {
    if (!appliedState) {
      return { ...others } as Params;
    }
    return {
      ...others,
      ...createConverter(appliedState, convert),
    } as Params;
  }, [appliedState, others]);

  return (
    children(params)
  );
};

export default HistoryFilterParams;
